import { createSelector } from "@reduxjs/toolkit";
import type {
  IRoomNumberingHeatmapEdge,
  IRoomNumberingHeatmapNode,
} from "@multiconsult-group/multiplacer-core";
import { openapi } from "../../openapi";
import type { IRootState } from "../app/store";
import {
  selectRoomNumberingHeatmapVisible,
  selectRoomNumberingRestrictionLines,
  selectRoomNumberingSelectedMethodKey,
  type IRoomNumberingRestrictionLine,
} from "./roomNumberingSlice";

export const selectRoomNumberingCalculationMetadataApiArg = createSelector(
  [
    (state: IRootState) => state.design.projectNo,
    (state: IRootState) => state.design.floorAndJob?.floorGlobalId,
    selectRoomNumberingSelectedMethodKey,
  ],
  (projectNo, floorGlobalId, methodKey) => {
    if (!projectNo || !floorGlobalId) {
      return null;
    }

    return {
      projectNo,
      methodKey,
      floorGlobalId,
    };
  },
);

export const selectCachedRoomNumberingCalculationMetadata = (state: IRootState) => {
  const apiArg = selectRoomNumberingCalculationMetadataApiArg(state);
  if (apiArg == null) return undefined;

  return openapi.endpoints.getMultiplacerRoomsRoomNumberingCalculationMetadata.select(apiArg)(state).data;
};

export type INrhRoomNumberingCalculationRequestBody = {
  projectNo: number;
  jobId: string;
  floorGlobalId: string;
  restrictionLines: IRoomNumberingRestrictionLine[];
  numberingOrigin: [number, number] | null;
};

export const getNrhRoomNumberingCalculationFixedCacheKey = (
  body: INrhRoomNumberingCalculationRequestBody,
) => `nrh-room-numbering:${body.projectNo}:${body.jobId}:${body.floorGlobalId}`;

export const selectCalculateNrhRoomNumberingRequestBody = createSelector(
  [
    (state: IRootState) => state.design.projectNo,
    (state: IRootState) => state.design.floorAndJob,
    selectRoomNumberingRestrictionLines,
    (state: IRootState) => state.roomNumbering.numberingOrigin,
  ],
  (projectNo, floorAndJob, restrictionLines, numberingOrigin) => {
    if (!projectNo || !floorAndJob?.jobId || !floorAndJob.floorGlobalId) {
      return null;
    }

    return {
      projectNo,
      jobId: floorAndJob.jobId,
      floorGlobalId: floorAndJob.floorGlobalId,
      restrictionLines,
      numberingOrigin,
    };
  },
);

export const selectCachedNrhRoomNumberingCalculationResult = (state: IRootState) => {
  const requestBody = selectCalculateNrhRoomNumberingRequestBody(state);
  if (requestBody == null) return undefined;

  return openapi.endpoints.postMultiplacerRoomsRoomNumberingCalculateNrh.select({
    fixedCacheKey: getNrhRoomNumberingCalculationFixedCacheKey(requestBody),
    requestId: undefined,
  })(state).data;
};

export const selectHasCachedRoomNumberingHeatmap = createSelector(
  [selectCachedNrhRoomNumberingCalculationResult],
  (result): boolean => (result?.heatmap.edges.length ?? 0) > 0,
);

export const selectVisibleRoomNumberingHeatmapEdges = createSelector(
  [selectCachedNrhRoomNumberingCalculationResult, selectRoomNumberingHeatmapVisible],
  (result, visible): IRoomNumberingHeatmapEdge[] =>
    visible
      ? (result?.heatmap.edges as IRoomNumberingHeatmapEdge[] | undefined) ?? []
      : [],
);

export const selectVisibleRoomNumberingHeatmapNodes = createSelector(
  [selectCachedNrhRoomNumberingCalculationResult, selectRoomNumberingHeatmapVisible],
  (result, visible): IRoomNumberingHeatmapNode[] =>
    visible
      ? (result?.heatmap.nodes as IRoomNumberingHeatmapNode[] | undefined) ?? []
      : [],
);
